import React, { useEffect, useRef, useState } from 'react';
import { resolveChatMessageRowPreset } from './chatMessageRowPresets';
import '../../../../css/common/chat/ChatMessageStack.css';

const REACTION_PICKER_VISIBILITY_EVENT = 'chat-reaction-picker-visibility-change';

const normalizeClassName = (value) =>
  String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');

const buildClassName = (...tokens) =>
  tokens
    .map((token) => normalizeClassName(token))
    .filter(Boolean)
    .join(' ');

const ChatMessageRow = ({
  messageId = '',
  isSelf = false,
  senderName = '',
  senderContent = null,
  showSender = true,
  readCount = 0,
  readLabel = '',
  timeLabel = '',
  variant = 'im',
  preset = '',
  classMap = null,
  bubbleClassName = '',
  metaClassName = '',
  rowClassName = '',
  anchorClassName = '',
  anchorActive = false,
  avatarNode = null,
  beforeLineContent = null,
  metaExtra = null,
  metaEndContent = null,
  rowStyle = undefined,
  onContextMenu = null,
  onTouchStart = null,
  onTouchEnd = null,
  onTouchCancel = null,
  onTouchMove = null,
  sideActionContent = null,
  footerContent = null,
  children = null,
}) => {
  const rowRef = useRef(null);
  const hoverHideTimerRef = useRef(null);
  const [isBubbleHovered, setIsBubbleHovered] = useState(false);
  const [isSideActionPinned, setIsSideActionPinned] = useState(false);
  const baseMap = resolveChatMessageRowPreset(preset, variant);
  const map = classMap && typeof classMap === 'object' ? { ...baseMap, ...classMap } : baseMap;
  const normalizedReadCount = Math.max(0, Number(readCount) || 0);
  const normalizedRowClassName = normalizeClassName(rowClassName);
  const suppressSideActionForCopySelection =
    normalizedRowClassName.includes('chat-message-row--copyActive') ||
    normalizedRowClassName.includes('chat-message-row--copyDimmed');
  const hasTime = String(timeLabel || '').trim().length > 0;
  const hasMetaExtra = metaExtra != null;
  const hasMetaEndContent = metaEndContent != null;
  const showReadCount = normalizedReadCount > 0;
  // リアクション集計は時刻/既読と同じメタ行へ並べる（バブルより広くなる場合のはみ出しは許容）
  const showMetaArea = hasTime || hasMetaExtra || showReadCount || hasMetaEndContent;
  const hasSideAction = !suppressSideActionForCopySelection && sideActionContent != null;
  const isSideActionVisible = hasSideAction && (isBubbleHovered || isSideActionPinned);
  const rowClass = buildClassName(
    'chat-message-row',
    isSelf ? 'chat-message-row--self' : 'chat-message-row--other',
    isSideActionVisible ? 'chat-message-row--sideActionVisible' : '',
    map.row,
    isSelf ? map.rowSelf : map.rowOther,
    rowClassName,
    anchorActive ? anchorClassName : ''
  );
  const bubbleClass = buildClassName(map.bubble, bubbleClassName);
  const metaWrapClass = buildClassName(map.timeWrap, metaClassName);
  const resolvedSenderName = String(senderName || '').trim();
  const hasSenderContent = senderContent != null;
  const showSenderLabel = Boolean(showSender && !isSelf && (hasSenderContent || resolvedSenderName));
  const resolvedReadLabel = String(readLabel || '').trim() || `既読 ${normalizedReadCount}`;
  const hasReservedSideSlot = hasSideAction;
  const handleContextMenu = typeof onContextMenu === 'function' ? onContextMenu : undefined;
  const handleTouchStart = typeof onTouchStart === 'function' ? onTouchStart : undefined;
  const handleTouchEnd = typeof onTouchEnd === 'function' ? onTouchEnd : undefined;
  const handleTouchCancel = typeof onTouchCancel === 'function' ? onTouchCancel : undefined;
  const handleTouchMove = typeof onTouchMove === 'function' ? onTouchMove : undefined;

  useEffect(() => {
    if (!hasSideAction) return undefined;
    const rowNode = rowRef.current;
    if (!rowNode) return undefined;
    const handlePickerVisibility = (event) => {
      const eventMessageId = String(event?.detail?.messageId || '');
      if (!eventMessageId || eventMessageId !== String(messageId || '')) return;
      const nextOpen = Boolean(event?.detail?.open);
      if (nextOpen) {
        clearHoverHideTimer();
      }
      setIsSideActionPinned(nextOpen);
    };
    rowNode.addEventListener(REACTION_PICKER_VISIBILITY_EVENT, handlePickerVisibility);
    return () => rowNode.removeEventListener(REACTION_PICKER_VISIBILITY_EVENT, handlePickerVisibility);
  }, [hasSideAction, messageId]);

  useEffect(() => () => {
    if (hoverHideTimerRef.current) {
      clearTimeout(hoverHideTimerRef.current);
      hoverHideTimerRef.current = null;
    }
  }, []);

  const clearHoverHideTimer = () => {
    if (!hoverHideTimerRef.current) return;
    clearTimeout(hoverHideTimerRef.current);
    hoverHideTimerRef.current = null;
  };

  const handleBubbleMouseEnter = () => {
    if (!hasSideAction) return;
    clearHoverHideTimer();
    setIsBubbleHovered(true);
  };

  const handleBubbleMouseLeave = () => {
    if (!hasSideAction) return;
    if (isSideActionPinned) {
      setIsBubbleHovered(false);
      return;
    }
    clearHoverHideTimer();
    hoverHideTimerRef.current = window.setTimeout(() => {
      setIsBubbleHovered(false);
      hoverHideTimerRef.current = null;
    }, 1500);
  };

  const metaTimeNode = hasTime ? (
    <span className={buildClassName('chat-message-row__metaTime', map.time)}>{timeLabel}</span>
  ) : null;
  const metaReadNode = showReadCount ? (
    <span className={buildClassName('chat-message-row__metaRead', map.read)}>{resolvedReadLabel}</span>
  ) : null;
  const metaReactionsNode = hasMetaEndContent ? (
    <span className="chat-message-row__metaReactions" role="group" aria-label="リアクション集計">
      {metaEndContent}
    </span>
  ) : null;

  const renderSideActionSlot = (position) => {
    const shouldRenderHere = position === 'self' ? isSelf : !isSelf;
    if (!shouldRenderHere) return null;
    if (!hasSideAction) return null;
    return (
      <span
        className={buildClassName(
          'chat-message-row__sideActionSlot',
          isSelf ? 'chat-message-row__sideActionSlot--self' : 'chat-message-row__sideActionSlot--other',
        )}
        onMouseEnter={handleBubbleMouseEnter}
        onMouseLeave={handleBubbleMouseLeave}
      >
        <span className="chat-message-row__sideAction">{sideActionContent}</span>
      </span>
    );
  };

  return (
    <div
      ref={rowRef}
      data-mid={messageId || undefined}
      className={rowClass}
      style={rowStyle}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      onTouchMove={handleTouchMove}
    >
      {!isSelf ? avatarNode : null}

      <div className={map.body}>
        {showSenderLabel ? (
          <span className={map.sender}>{hasSenderContent ? senderContent : resolvedSenderName}</span>
        ) : null}
        {beforeLineContent}

        <div
          className={buildClassName(
            map.line,
            'chat-message-row__line',
            hasReservedSideSlot ? 'chat-message-row__line--withSideActionSlot' : ''
          )}
        >
          {/* バブル・リンクプレビュー・メタ行をひとつの幅（＝バブル幅）へ束ねる。
              ホバーのリアクション追加スロットは束の中に入れたうえで、メタ行側でその分を差し引く。 */}
          <div
            className={buildClassName(
              'chat-message-row__bubbleWrap',
              isSideActionVisible ? 'chat-message-row__bubbleWrap--sideActionVisible' : '',
            )}
            onMouseEnter={hasSideAction ? handleBubbleMouseEnter : undefined}
            onMouseLeave={hasSideAction ? handleBubbleMouseLeave : undefined}
          >
            <div className="chat-message-row__bubbleRow">
              {renderSideActionSlot('self')}
              <div className={bubbleClass}>{children}</div>
              {renderSideActionSlot('other')}
            </div>

            {footerContent ? (
              <div className={buildClassName(
                'chat-message-row__footer',
                isSelf ? 'chat-message-row__footer--self' : 'chat-message-row__footer--other',
              )}>
                {footerContent}
              </div>
            ) : null}

            {showMetaArea ? (
              <div className={buildClassName(
                'chat-message-row__meta',
                isSelf ? 'chat-message-row__meta--self' : 'chat-message-row__meta--other',
                hasReservedSideSlot ? 'chat-message-row__meta--withSideActionSlot' : '',
                metaWrapClass,
              )} role="group" aria-label="メッセージのメタ情報">
                {isSelf ? (
                  <>
                    {metaReactionsNode}
                    {metaReadNode}
                    {metaExtra}
                    {metaTimeNode}
                  </>
                ) : (
                  <>
                    {metaTimeNode}
                    {metaExtra}
                    {metaReadNode}
                    {metaReactionsNode}
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessageRow;
